import { useCallback, useSyncExternalStore } from 'react'

/** Classi di dimensione della finestra M3 (window size classes). */
export type WindowClass = 'compact' | 'medium' | 'expanded' | 'large' | 'xlarge'

/** Soglie minime in px: medium 600, expanded 840, large 1200, extra-large 1600. */
export const WINDOW_BREAKPOINTS = { medium: 600, expanded: 840, large: 1200, xlarge: 1600 } as const

const QUERIES = [
  `(min-width: ${WINDOW_BREAKPOINTS.medium}px)`,
  `(min-width: ${WINDOW_BREAKPOINTS.expanded}px)`,
  `(min-width: ${WINDOW_BREAKPOINTS.large}px)`,
  `(min-width: ${WINDOW_BREAKPOINTS.xlarge}px)`,
] as const

const CLASSES: readonly WindowClass[] = ['compact', 'medium', 'expanded', 'large', 'xlarge']

function current(): WindowClass {
  let i = 0
  for (const q of QUERIES) { if (window.matchMedia(q).matches) i += 1 }
  return CLASSES[i] ?? 'compact'
}

/** Classe di finestra attuale, aggiornata con matchMedia (nessun listener di resize). */
export function useWindowClass(): WindowClass {
  const subscribe = useCallback((cb: () => void) => {
    const lists = QUERIES.map(q => window.matchMedia(q))
    lists.forEach(l => l.addEventListener('change', cb))
    return () => lists.forEach(l => l.removeEventListener('change', cb))
  }, [])
  return useSyncExternalStore(subscribe, current, () => 'large')
}

/** Rail standard (expanded rail) fisso: large e extra-large. */
export const hasStandardRail = (c: WindowClass): boolean => c === 'large' || c === 'xlarge'

/** Cover screen dei pieghevoli (280-359): sottoclasse di compact con la barra applicazione ridotta (D-F1). */
export const COMPACT_NARROW_MAX = 359

const NARROW_QUERY = `(max-width: ${COMPACT_NARROW_MAX}px)`

export function useIsCompactNarrow(): boolean {
  const subscribe = useCallback((cb: () => void) => {
    const l = window.matchMedia(NARROW_QUERY)
    l.addEventListener('change', cb)
    return () => l.removeEventListener('change', cb)
  }, [])
  return useSyncExternalStore(subscribe, () => window.matchMedia(NARROW_QUERY).matches, () => false)
}
